import {
  Button,
  Checkbox,
  ConfigProvider,
  DatePicker,
  DatePickerProps,
  Divider,
  Flex,
  Form,
  InputNumber,
  Radio,
  Tooltip,
  Typography,
} from "antd";
import { Dayjs } from "dayjs";
import { useEffect, useMemo } from "react";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { InstructorAvailability } from "shared/types";
import {
  combineDateTime,
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIMEZONE,
  now,
  tzDate,
} from "shared/utils/dayjs";
import { AutoCloseTimePicker } from "./AutoCloseTimePicker";
import { buildAvailabilityDisabledTimeForWeekday } from "./buildAvailabilityDisabledTime";
import { textPx } from "shared/constants/zoom";

// Exported for the drawer's per-day-times submit (series grouping + the ghost
// preview both map weekday numbers/codes). The labels are fallbacks; render
// sites map them through translate(`label.day_short.${value.toLowerCase()}`)
// so the pills follow the language.
export const DAY_OPTIONS = [
  { label: "SEN", value: "MO" },
  { label: "SEL", value: "TU" },
  { label: "RAB", value: "WE" },
  { label: "KAM", value: "TH" },
  { label: "JUM", value: "FR" },
  { label: "SAB", value: "SA" },
  { label: "MIN", value: "SU" },
];

export const DAYJS_TO_RRULE_DAY: Record<number, string> = {
  0: "SU",
  1: "MO",
  2: "TU",
  3: "WE",
  4: "TH",
  5: "FR",
  6: "SA",
};

// Inverse of the above — needed to look up each per-day time picker's own
// dayjs weekday number (buildAvailabilityDisabledTimeForWeekday's input)
// from its rrule day code.
const RRULE_DAY_TO_DAYJS: Record<string, number> = Object.fromEntries(
  Object.entries(DAYJS_TO_RRULE_DAY).map(([day, code]) => [code, Number(day)]),
);

// Same picker constants as TimeSection's start-time field (not imported from
// there: TimeSection imports this file, so that would be a cycle).
const TIME_FORMAT = "HH:mm";
const TIME_MINUTE_STEP = 5;

// The first calendar date, on/after `selected`, that lands on `dayCode`'s
// weekday — e.g. picking Monday as the start date with Wednesday also
// selected means Wednesday's own first occurrence is 2 days later, not the
// same day as Monday's. Needed below so each per-day picker can disable past
// times against ITS OWN actual first occurrence, not the shared start date.
const getFirstOccurrenceDateForDay = (selected: Dayjs, dayCode: string): Dayjs => {
  const targetWeekday = RRULE_DAY_TO_DAYJS[dayCode];
  let cursor = selected.startOf("day");
  for (let i = 0; i < 7; i++) {
    if (cursor.day() === targetWeekday) return cursor;
    cursor = cursor.add(1, "day");
  }
  return selected;
};

// Hook-free equivalent of useDisabledStartTime's past-time math — needed
// here since each per-day picker needs its own date (that day's own first
// occurrence, per getFirstOccurrenceDateForDay above), and hooks can't be
// called a variable number of times inside DAY_OPTIONS.map(). Only disables
// anything when `date` is today; any other (future) date has no past times.
const disabledPastTimeForDate = (date: Dayjs | undefined) => {
  const disabledHours: number[] = [];
  const disabledMinutesByHour: Record<number, number[]> = {};
  const isToday = date?.isSame(now(), "day") ?? false;
  if (date && isToday) {
    const earliestStart = now();
    const dayStart = date.startOf("day");
    for (let h = 0; h < 24; h++) {
      const disabledMinutes: number[] = [];
      for (let m = 0; m < 60; m++) {
        if (dayStart.hour(h).minute(m).isBefore(earliestStart)) disabledMinutes.push(m);
      }
      disabledMinutesByHour[h] = disabledMinutes;
      if (dayStart.hour(h).minute(59).isBefore(earliestStart)) disabledHours.push(h);
    }
  }
  return {
    disabledHours: () => disabledHours,
    disabledMinutes: (hour: number) => disabledMinutesByHour[hour] ?? [],
  };
};

const getFiniteMaxOccurrences = (maxOccurrences?: number) =>
  maxOccurrences !== undefined && maxOccurrences !== Infinity
    ? maxOccurrences
    : undefined;

const getMinimumOccurrenceCount = (
  recurrenceType: string,
  recurrenceDays?: string[],
) =>
  recurrenceType === "weekly" ? Math.max(2, recurrenceDays?.length ?? 1) : 2;

// Counts how many occurrences fit from the selected start date through package
// expiry, inclusive. Weekly rules only count selected weekdays on/after the
// selected date, so earlier weekdays in the same week are skipped.
const getExpiryBoundedOccurrenceCount = (
  recurrenceType: string,
  selectedDate?: Dayjs,
  maxScheduleDate?: Date,
  recurrenceDays?: string[],
) => {
  if (!selectedDate || !maxScheduleDate) return undefined;

  // Normalize the AntD-sourced picker value onto our plugin-extended instance
  // before any date math — see exceedsPackageBounds' note.
  const start = tzDate(selectedDate);
  const lastAllowedDate = tzDate(maxScheduleDate).endOf("day");
  if (start.isAfter(lastAllowedDate)) return 0;

  if (recurrenceType === "daily") {
    return lastAllowedDate.diff(start.startOf("day"), "day") + 1;
  }

  if (recurrenceType !== "weekly") return undefined;

  const allowedDays = new Set(
    recurrenceDays?.length
      ? recurrenceDays
      : [DAYJS_TO_RRULE_DAY[start.day()]],
  );
  let count = 0;
  let cursor = start.startOf("day");
  while (!cursor.isAfter(lastAllowedDate, "day")) {
    if (allowedDays.has(DAYJS_TO_RRULE_DAY[cursor.day()])) count += 1;
    cursor = cursor.add(1, "day");
  }
  return count;
};

const getLastOccurrenceDateByCount = (
  recurrenceType: string,
  selectedDate?: Dayjs,
  maxOccurrences?: number,
  recurrenceDays?: string[],
) => {
  const finiteMaxOccurrences = getFiniteMaxOccurrences(maxOccurrences);
  if (!selectedDate || finiteMaxOccurrences === undefined) return undefined;

  // Normalize the AntD-sourced picker value onto our plugin-extended instance
  // before any date math — see exceedsPackageBounds' note.
  const start = tzDate(selectedDate);
  if (finiteMaxOccurrences <= 0) return start.subtract(1, "day");

  if (recurrenceType === "daily") {
    return start.add(finiteMaxOccurrences - 1, "day");
  }

  if (recurrenceType !== "weekly") return undefined;

  const allowedDays = new Set(
    recurrenceDays?.length
      ? recurrenceDays
      : [DAYJS_TO_RRULE_DAY[start.day()]],
  );
  let count = 0;
  let cursor = start.startOf("day");

  while (count < finiteMaxOccurrences) {
    if (allowedDays.has(DAYJS_TO_RRULE_DAY[cursor.day()])) {
      count += 1;
      if (count === finiteMaxOccurrences) return cursor;
    }
    cursor = cursor.add(1, "day");
  }

  return undefined;
};

// Earliest end date that still satisfies the minimum-occurrence rule (2 for
// daily, the selected-day count for weekly). It's the date of the Nth occurrence
// where N is that minimum — anything before it would yield too few sessions.
const getMinimumUntilDate = (
  recurrenceType: string,
  selectedDate?: Dayjs,
  recurrenceDays?: string[],
) =>
  getLastOccurrenceDateByCount(
    recurrenceType,
    selectedDate,
    getMinimumOccurrenceCount(recurrenceType, recurrenceDays),
    recurrenceDays,
  );

// Both inputs are normalized through tzDate, so the isBefore comparison never
// mixes a raw/AntD-sourced Dayjs with one of ours — see exceedsPackageBounds.
const getEarlierDate = (
  first?: Dayjs | Date,
  second?: Dayjs | Date,
): Dayjs | undefined => {
  if (!first && !second) return undefined;
  if (!first) return tzDate(second);
  if (!second) return tzDate(first);

  const firstDate = tzDate(first);
  const secondDate = tzDate(second);
  return firstDate.isBefore(secondDate) ? firstDate : secondDate;
};

// The pickers below bound the recurrence by package credits/expiry, but the
// create panel lets time be set before packages are picked — so already-entered
// values can exceed bounds that only arrive later. This re-checks the same
// limits (reusing the picker computations) so submission can be gated on it.
// Per-day times don't change this math: it counts occurrences by DAY over the
// union of selected weekdays, which equals the sum across the per-time series
// the submit emits (count mode is N TOTAL across all days).
export const exceedsPackageBounds = ({
  recurrenceType,
  scheduleDate,
  recurrenceDays,
  endConditionType,
  endConditionCount,
  endConditionUntil,
  maxOccurrences,
  maxScheduleDate,
}: {
  recurrenceType: string;
  scheduleDate?: Dayjs;
  recurrenceDays?: string[];
  endConditionType?: string;
  endConditionCount?: number;
  endConditionUntil?: Dayjs;
  maxOccurrences?: number;
  maxScheduleDate?: Date;
}): boolean => {
  const finiteMaxOccurrences = getFiniteMaxOccurrences(maxOccurrences);
  if (finiteMaxOccurrences === undefined && !maxScheduleDate) return false;
  if (!scheduleDate) return false;

  // scheduleDate / endConditionUntil arrive straight from AntD form fields,
  // whose DatePicker emits Dayjs objects built by AntD's OWN bundled dayjs (no
  // utc/timezone plugins). Mixing those with our tzDate(...) results in a
  // .diff/.isAfter/.startOf chain makes the plugin code call .tz()/.local() on
  // the plugin-less instance and throw. Normalize both onto our instance up
  // front so every comparison below runs on plugin-extended Dayjs values.
  const start = tzDate(scheduleDate);
  const until = endConditionUntil ? tzDate(endConditionUntil) : undefined;

  // The start date itself must fall within every selected package's validity.
  if (maxScheduleDate && start.isAfter(tzDate(maxScheduleDate).endOf("day"))) {
    return true;
  }
  if (finiteMaxOccurrences !== undefined && finiteMaxOccurrences < 1) {
    return true;
  }
  if (recurrenceType !== "daily" && recurrenceType !== "weekly") return false;

  if (endConditionType === "count") {
    const expiryMaxOccurrences = getExpiryBoundedOccurrenceCount(
      recurrenceType,
      start,
      maxScheduleDate,
      recurrenceDays,
    );
    const maximumOccurrences =
      finiteMaxOccurrences !== undefined
        ? Math.min(
            finiteMaxOccurrences,
            expiryMaxOccurrences ?? finiteMaxOccurrences,
          )
        : expiryMaxOccurrences;
    return (
      maximumOccurrences !== undefined &&
      (endConditionCount ?? 0) > maximumOccurrences
    );
  }

  const maximumUntilDate = getEarlierDate(
    maxScheduleDate,
    getLastOccurrenceDateByCount(
      recurrenceType,
      start,
      finiteMaxOccurrences,
      recurrenceDays,
    ),
  );
  return Boolean(
    maximumUntilDate && until && until.isAfter(maximumUntilDate, "day"),
  );
};

// True when a chosen "until" end date is too close to the start to satisfy the
// minimum-occurrence rule (≥2 sessions). Mirrors the picker's disabledDate min
// bound so the save button and the field validator agree. Only the until path
// can underflow — the count path is already floored by its min rule.
export const isUntilDateTooClose = ({
  recurrenceType,
  scheduleDate,
  recurrenceDays,
  endConditionUntil,
}: {
  recurrenceType: string;
  scheduleDate?: Dayjs;
  recurrenceDays?: string[];
  endConditionUntil?: Dayjs;
}): boolean => {
  if (recurrenceType !== "daily" && recurrenceType !== "weekly") return false;
  if (!scheduleDate || !endConditionUntil) return false;
  const minimumUntilDate = getMinimumUntilDate(
    recurrenceType,
    scheduleDate,
    recurrenceDays,
  );
  return Boolean(
    minimumUntilDate &&
      tzDate(endConditionUntil).isBefore(minimumUntilDate, "day"),
  );
};

interface RecurrenceSelectProps {
  selectedDate?: Dayjs;
  maxOccurrences?: number;
  maxScheduleDate?: Date;
  // Passed by TimeSection.tsx so each per-day time picker below can check
  // itself against the SAME instructor-availability data the shared
  // "Waktu Mulai" field uses, just narrowed to its own weekday.
  availability?: InstructorAvailability;
  duration?: number;
  // Fires whenever "at least one currently-selected day is fully blocked by
  // instructor availability" changes — forwarded up through TimeSection so
  // CreateScheduleDrawer.tsx can gate Jadwalkan on it.
  onPerDayBlockChange?: (blocked: boolean) => void;
}

export const RecurrenceSelect = ({
  selectedDate,
  maxOccurrences,
  maxScheduleDate,
  availability,
  duration,
  onPerDayBlockChange,
}: RecurrenceSelectProps) => {
  const form = Form.useFormInstance();
  const recurrenceType = Form.useWatch("recurrence_type", form);
  const recurrenceDays = Form.useWatch<string[] | undefined>(
    "recurrence_days",
    form,
  );
  const perDayTimesActive = !!Form.useWatch("per_day_times", form);

  // Each weekday's own availability grid, keyed by its rrule code — computed
  // once here (top-level, so it can use hooks) rather than inside the
  // render-prop below. Every DAY_OPTIONS entry gets an entry regardless of
  // current selection; only the currently-selected days actually render or
  // factor into the "any day blocked" signal.
  const perDayAvailability = useMemo(() => {
    const map = new Map<
      string,
      ReturnType<typeof buildAvailabilityDisabledTimeForWeekday>
    >();
    if (!perDayTimesActive) return map;
    DAY_OPTIONS.forEach((opt) => {
      map.set(
        opt.value,
        buildAvailabilityDisabledTimeForWeekday(
          availability,
          duration ?? 0,
          RRULE_DAY_TO_DAYJS[opt.value],
        ),
      );
    });
    return map;
  }, [perDayTimesActive, availability, duration]);

  const isDayFullyBlocked = (code: string) =>
    (perDayAvailability.get(code)?.disabledTime().disabledHours().length ?? 0) >= 24;

  // Reports "at least one currently-selected day is fully blocked" up to
  // TimeSection.tsx, which folds it together with the shared field's own
  // noValidTime before forwarding a single combined signal to
  // CreateScheduleDrawer.tsx.
  useEffect(() => {
    const selectedDays = recurrenceDays ?? [];
    onPerDayBlockChange?.(
      perDayTimesActive && selectedDays.some((code) => isDayFullyBlocked(code)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perDayTimesActive, recurrenceDays, perDayAvailability, onPerDayBlockChange]);

  // Per-day stale-selection guard — same principle as TimeSection's own
  // effect for the shared field: if a day's picked time is no longer valid
  // for THAT day's instructor/availability, clear it, UNLESS the day is
  // fully blocked (no time at all would work), in which case the value is
  // preserved and the picker's own disabled state communicates the conflict
  // instead of silently emptying the field.
  useEffect(() => {
    if (!perDayTimesActive) return;
    (recurrenceDays ?? []).forEach((code) => {
      if (isDayFullyBlocked(code)) return;
      const fieldName = `day_time_${code}`;
      const current = form.getFieldValue(fieldName) as Dayjs | undefined;
      if (!current) return;
      const check = perDayAvailability.get(code)?.disabledTime();
      if (!check) return;
      const hour = current.hour();
      const minute = current.minute();
      const isNowInvalid =
        check.disabledHours().includes(hour) ||
        check.disabledMinutes(hour).includes(minute);
      if (isNowInvalid) {
        form.setFieldValue(fieldName, undefined);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perDayTimesActive, recurrenceDays, perDayAvailability, form]);

  const finiteMaxOccurrences = getFiniteMaxOccurrences(maxOccurrences);

  // A package that pays for a single session of this class (one credit left,
  // or fewer credits than two sessions cost) can never back a valid recurring
  // schedule (which needs ≥2 sessions), so repeating is disabled entirely.
  const cannotRepeat = finiteMaxOccurrences === 1;

  // selectedDate is an AntD-sourced Dayjs (plugin-less). Normalize once onto our
  // instance so every render-side date computation below stays on plugin-aware
  // Dayjs values — see exceedsPackageBounds' note.
  const normalizedSelectedDate = selectedDate ? tzDate(selectedDate) : undefined;

  // A start before NOW is a backdated schedule, which is single-occurrence
  // only (the server rejects a recurrence with a past DTSTART): a backdated
  // series would materialize every historical occurrence — and hold a credit
  // for each — in one write, and the end-date picker below can't express a
  // series that already ended anyway. Instant granularity, not day: a class
  // recorded at 14:00 for 10:00 this morning is a backfill too. Until a time
  // is picked, only a day before today counts.
  const startTime = Form.useWatch("start_time", form) as Dayjs | undefined;
  const combinedStart = combineDateTime(selectedDate, startTime, DEFAULT_TIMEZONE);
  const pastStart =
    !!normalizedSelectedDate &&
    (normalizedSelectedDate.isBefore(now(), "day") ||
      (!!combinedStart && combinedStart.isBefore(now())));
  const repeatDisabled = cannotRepeat || pastStart;

  const defaultDay = normalizedSelectedDate
    ? DAYJS_TO_RRULE_DAY[normalizedSelectedDate.day()]
    : undefined;

  // Earliest end date that still yields the minimum number of sessions — used to
  // clear an end date that the start date has overtaken (see effect below).
  const minimumUntilDate =
    recurrenceType === "daily" || recurrenceType === "weekly"
      ? getMinimumUntilDate(recurrenceType, normalizedSelectedDate, recurrenceDays)
      : undefined;

  // The start date must always be a valid occurrence of the rrule, otherwise
  // the backend rejects the schedule. Force-include the start date's weekday
  // in recurrence_days whenever weekly mode is active.
  useEffect(() => {
    if (recurrenceType !== "weekly" || !defaultDay) return;
    const days = recurrenceDays ?? [];
    if (!days.includes(defaultDay)) {
      form.setFieldsValue({ recurrence_days: [...days, defaultDay] });
    }
  }, [recurrenceType, recurrenceDays, defaultDay, form]);

  // If repeating becomes impossible (package down to 1 credit, or the start
  // date moved into the past) while daily/weekly is selected, snap back to
  // "does not repeat" so no invalid recurrence lingers.
  useEffect(() => {
    if (
      repeatDisabled &&
      (recurrenceType === "daily" || recurrenceType === "weekly")
    ) {
      form.setFieldsValue({ recurrence_type: "none" });
    }
  }, [repeatDisabled, recurrenceType, form]);

  // When the start date moves forward past the current end date (or past the
  // earliest end date that still satisfies the min-session rule), clear the end
  // date so the user must re-pick a valid one — rather than silently keeping a
  // now-invalid value that only errors at submit.
  useEffect(() => {
    if (!minimumUntilDate) return;
    const currentUntil = form.getFieldValue("end_condition_until") as
      | Dayjs
      | undefined;
    if (currentUntil && tzDate(currentUntil).isBefore(minimumUntilDate, "day")) {
      form.setFieldsValue({ end_condition_until: undefined });
    }
  }, [minimumUntilDate, form]);

  return (
    <>
      <Typography.Text strong style={{ display: "block", marginBottom: 20 }}>
        {translate("schedule.recurrence.title")}
      </Typography.Text>
      <Form.Item name="recurrence_type" initialValue="none" noStyle>
        <Radio.Group
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 15,
          }}
        >
          <Radio value="none">
            {translate("schedule.recurrence.does_not_repeat")}
          </Radio>
          {repeatDisabled ? (
            // Disabled radios swallow hover, so the Tooltip wraps an enabled
            // container that keeps the explanation reachable.
            <Tooltip
              title={translate(
                pastStart
                  ? "schedule.recurrence.past_start_no_repeat"
                  : "schedule.recurrence.single_credit_no_repeat"
              )}
            >
              <Flex vertical gap={15}>
                <Radio value="daily" disabled>
                  {translate("schedule.recurrence.daily")}
                </Radio>
                <Radio value="weekly" disabled>
                  {translate("schedule.recurrence.weekly")}
                </Radio>
              </Flex>
            </Tooltip>
          ) : (
            <>
              <Radio value="daily">
                {translate("schedule.recurrence.daily")}
              </Radio>
              <Radio value="weekly">
                {translate("schedule.recurrence.weekly")}
              </Radio>
            </>
          )}
        </Radio.Group>
      </Form.Item>

      {/* Day-of-week toggles for weekly */}
      <Form.Item
        noStyle
        shouldUpdate={(prev, cur) =>
          prev.recurrence_type !== cur.recurrence_type
        }
      >
        {({ getFieldValue }) => {
          if (getFieldValue("recurrence_type") !== "weekly") return null;

          return (
            <Form.Item
              name="recurrence_days"
              initialValue={defaultDay ? [defaultDay] : []}
              style={{ marginTop: 30 }}
              rules={[
                {
                  validator: (_, value?: string[]) => {
                    if (
                      finiteMaxOccurrences !== undefined &&
                      value &&
                      value.length > finiteMaxOccurrences
                    ) {
                      return Promise.reject(
                        new Error(
                          `${translate(
                            "form.error.exceed_maximum",
                          )} ${finiteMaxOccurrences}`,
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <DayOfWeekToggle requiredDay={defaultDay} />
            </Form.Item>
          );
        }}
      </Form.Item>

      {/* Per-day start times for weekly recurrences spanning 2+ days. Each
          distinct time becomes its own series at submit. All fields are
          preserve={false}, so deselecting a day (or leaving weekly / dropping
          to 1 day, which unmounts the whole block) clears its value instead of
          submitting a stale one. */}
      <Form.Item
        noStyle
        shouldUpdate={(prev, cur) =>
          prev.recurrence_type !== cur.recurrence_type ||
          prev.recurrence_days !== cur.recurrence_days ||
          prev.per_day_times !== cur.per_day_times ||
          prev.start_time !== cur.start_time
        }
      >
        {({ getFieldValue }) => {
          if (getFieldValue("recurrence_type") !== "weekly") return null;
          const days =
            (getFieldValue("recurrence_days") as string[] | undefined) ?? [];
          if (days.length < 2) return null;
          const perDayTimes = !!getFieldValue("per_day_times");

          return (
            <>
              {/* The default checkbox border is too faint for an opt-in the
                  admin must notice; scope a darker, slightly thicker box
                  border here (mirrors PackageRadioList's unselected radio
                  ring: ink3 at 1.5px). The provider wraps the Form.Item so
                  the checked/onChange injection still reaches the Checkbox. */}
              <ConfigProvider
                theme={{ token: { colorBorder: colors.ink3, lineWidth: 1.5 } }}
              >
                <Form.Item
                  name="per_day_times"
                  valuePropName="checked"
                  initialValue={false}
                  preserve={false}
                  style={{ marginTop: 12, marginBottom: perDayTimes ? 6 : 0 }}
                >
                  <Checkbox>
                    {translate("schedule.recurrence.different_time_per_day")}
                  </Checkbox>
                </Form.Item>
              </ConfigProvider>
              {perDayTimes && (
                <>
                  <Flex gap={12} wrap="wrap">
                    {DAY_OPTIONS.filter((opt) =>
                      days.includes(opt.value),
                    ).map((opt) => {
                      const dayResult = perDayAvailability.get(opt.value);
                      const dayBlocked = isDayFullyBlocked(opt.value);
                      const blockReason = dayResult?.impossibleDates[0]?.reason;
                      // Union of past-time disabling (only matters when this
                      // day's own first occurrence is today) and
                      // availability-based disabling — mirrors TimeSection's
                      // mergedStartTimeDisabled for the shared field, which
                      // this per-day picker previously had no equivalent of.
                      const pastDisabled = disabledPastTimeForDate(
                        normalizedSelectedDate
                          ? getFirstOccurrenceDateForDay(normalizedSelectedDate, opt.value)
                          : undefined,
                      );
                      const mergedDisabledTime = () => {
                        const avail = dayResult?.disabledTime() ?? {
                          disabledHours: () => [],
                          disabledMinutes: () => [],
                        };
                        const hours = new Set([
                          ...pastDisabled.disabledHours(),
                          ...avail.disabledHours(),
                        ]);
                        return {
                          disabledHours: () => Array.from(hours),
                          disabledMinutes: (hour: number) =>
                            Array.from(
                              new Set([
                                ...pastDisabled.disabledMinutes(hour),
                                ...avail.disabledMinutes(hour),
                              ]),
                            ),
                        };
                      };
                      return (
                        <div key={opt.value}>
                          <Form.Item
                            name={`day_time_${opt.value}`}
                            label={translate(
                              `label.day_short.${opt.value.toLowerCase()}`,
                            )}
                            preserve={false}
                            initialValue={getFieldValue("start_time")}
                            rules={[
                              {
                                required: true,
                                message: `${translate(
                                  "form.error.select",
                                )} ${translate("label.start_time")}`,
                              },
                            ]}
                            style={{ marginBottom: dayBlocked ? 4 : 8 }}
                          >
                            <AutoCloseTimePicker
                              format={TIME_FORMAT}
                              minuteStep={TIME_MINUTE_STEP}
                              allowClear={false}
                              style={{ width: 110 }}
                              disabled={dayBlocked}
                              disabledTime={mergedDisabledTime}
                            />
                          </Form.Item>
                          {dayBlocked && (
                            <div
                              style={{
                                fontSize: textPx(11),
                                lineHeight: 1.3,
                                color: colors.amberDark,
                                maxWidth: 110,
                                marginBottom: 8,
                              }}
                            >
                              {translate(
                                blockReason === "on_leave"
                                  ? "schedule.drawer.day_time_on_leave"
                                  : blockReason === "no_working_hours"
                                    ? "schedule.drawer.day_time_no_working_hours"
                                    : "schedule.drawer.day_time_unavailable_generic",
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </Flex>
                </>
              )}
            </>
          );
        }}
      </Form.Item>

      {/* End conditions for daily and weekly */}
      <Form.Item
        noStyle
        shouldUpdate={(prev, cur) =>
          prev.recurrence_type !== cur.recurrence_type ||
          prev.recurrence_days !== cur.recurrence_days
        }
      >
        {({ getFieldValue, setFieldsValue }) => {
          const recurrenceType = getFieldValue("recurrence_type");
          if (recurrenceType !== "weekly" && recurrenceType !== "daily")
            return null;
          const recurrenceDays = getFieldValue("recurrence_days") as
            | string[]
            | undefined;
          const minimumOccurrences = getMinimumOccurrenceCount(
            recurrenceType,
            recurrenceDays,
          );
          const expiryMaxOccurrences = getExpiryBoundedOccurrenceCount(
            recurrenceType,
            normalizedSelectedDate,
            maxScheduleDate,
            recurrenceDays,
          );
          const maximumOccurrences =
            finiteMaxOccurrences !== undefined
              ? Math.min(
                  finiteMaxOccurrences,
                  expiryMaxOccurrences ?? finiteMaxOccurrences,
                )
              : expiryMaxOccurrences;
          const creditBoundedEndDate = getLastOccurrenceDateByCount(
            recurrenceType,
            normalizedSelectedDate,
            finiteMaxOccurrences,
            recurrenceDays,
          );
          const maximumUntilDate = getEarlierDate(
            maxScheduleDate,
            creditBoundedEndDate,
          );
          const disabledUntilDate: DatePickerProps["disabledDate"] = (
            current,
          ) => {
            if (!current) return false;
            // current is an AntD-sourced calendar cell (its type lacks
            // .tz/.local) — reduce to a native Date and rebuild on our instance
            // before comparing against our tzDate-based bounds.
            const day = tzDate(current.toDate());
            if (day.valueOf() <= now().startOf("day").valueOf()) return true;
            if (
              normalizedSelectedDate &&
              day.valueOf() <= normalizedSelectedDate.startOf("day").valueOf()
            )
              return true;
            // Floor the picker at the minimum-valid end date so a too-close date
            // (fewer than the min sessions) can't be selected — the earliest
            // selectable date is the one yielding exactly the minimum count.
            if (
              minimumUntilDate &&
              day.valueOf() < minimumUntilDate.startOf("day").valueOf()
            )
              return true;
            if (
              maximumUntilDate &&
              day.valueOf() > maximumUntilDate.endOf("day").valueOf()
            )
              return true;
            return false;
          };
          const defaultUntilDate =
            maximumUntilDate && now().add(1, "month").isAfter(maximumUntilDate)
              ? maximumUntilDate
              : now().add(1, "month");
          const countDisplay =
            maximumOccurrences !== undefined
              ? ` (Min: ${minimumOccurrences}, Max: ${maximumOccurrences})`
              : ` (Min: ${minimumOccurrences})`;

          return (
            <>
              {/* Unlike TimeSection.tsx's divider before "Pengulangan" (which
                  trails a field with its own 12px bottom margin, so a 4px
                  top margin here is enough to match it), this divider
                  directly follows the recurrence Radio.Group, which has no
                  trailing margin of its own — needs the fuller gap itself. */}
              <Divider style={{ margin: "16px 0 12px" }} />
              <Typography.Text strong style={{ display: "block", marginBottom: 12 }}>
                {translate("schedule.recurrence.ends")}
              </Typography.Text>
              <Form.Item
                name="end_condition_type"
                initialValue="until"
              >
                <Radio.Group
                  optionType="button"
                  size="small"
                  style={{ display: "flex", width: "100%" }}
                  options={[
                    {
                      label: translate("schedule.recurrence.on_date"),
                      value: "until",
                      style: {
                        flex: 1,
                        textAlign: "center",
                        whiteSpace: "normal",
                        lineHeight: 1.4,
                        padding: "4px 8px",
                        height: "auto",
                      },
                    },
                    {
                      label: translate("schedule.recurrence.after_occurrences"),
                      value: "count",
                      style: {
                        flex: 1,
                        textAlign: "center",
                        whiteSpace: "normal",
                        lineHeight: 1.4,
                        padding: "4px 8px",
                        height: "auto",
                      },
                    },
                  ]}
                />
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prev, cur) =>
                  prev.end_condition_type !== cur.end_condition_type ||
                  prev.recurrence_type !== cur.recurrence_type ||
                  prev.recurrence_days !== cur.recurrence_days
                }
              >
                {({ getFieldValue: gfv }) => {
                  const endType = gfv("end_condition_type");

                  if (endType === "until") {
                    const currentUntil = gfv("end_condition_until") as
                      | Dayjs
                      | undefined;
                    if (
                      maximumUntilDate &&
                      currentUntil &&
                      tzDate(currentUntil).isAfter(maximumUntilDate, "day")
                    ) {
                      setTimeout(
                        () =>
                          setFieldsValue({
                            end_condition_until: maximumUntilDate,
                          }),
                        0,
                      );
                    }

                    return (
                      <Form.Item
                        name="end_condition_until"
                        label={translate("schedule.recurrence.ends_on_date")}
                        rules={[
                          {
                            required: true,
                            message: `${translate(
                              "form.error.select",
                            )} ${translate("label.schedule_date")}`,
                          },
                          {
                            validator: (_, value?: Dayjs) =>
                              isUntilDateTooClose({
                                recurrenceType,
                                scheduleDate: selectedDate,
                                recurrenceDays,
                                endConditionUntil: value,
                              })
                                ? Promise.reject(
                                    new Error(
                                      translate(
                                        "schedule.recurrence.end_date_too_close",
                                      ),
                                    ),
                                  )
                                : Promise.resolve(),
                          },
                        ]}
                        initialValue={defaultUntilDate}
                      >
                        <DatePicker
                          style={{ width: "100%" }}
                          format={DEFAULT_DATE_FORMAT}
                          showToday={false}
                          disabledDate={disabledUntilDate}
                        />
                      </Form.Item>
                    );
                  }

                  if (endType === "count") {
                    return (
                      <Form.Item
                        name="end_condition_count"
                        label={`${translate(
                          "schedule.recurrence.occurrences_count_label",
                        )}${countDisplay}`}
                        rules={[
                          {
                            required: true,
                            message: `${translate(
                              "form.error.input",
                            )} ${translate(
                              "schedule.recurrence.occurrences_count_label",
                            )}`,
                          },
                          {
                            type: "number",
                            min: minimumOccurrences,
                            message: `${translate(
                              "form.error.fall_behind_minimum",
                            )} ${minimumOccurrences}`,
                          },
                          ...(maximumOccurrences !== undefined
                            ? [
                                {
                                  type: "number" as const,
                                  max: maximumOccurrences,
                                  message: `${translate(
                                    "form.error.exceed_maximum",
                                  )} ${maximumOccurrences}`,
                                },
                              ]
                            : []),
                        ]}
                        initialValue={minimumOccurrences}
                      >
                        <InputNumber
                          style={{ width: "100%" }}
                          min={minimumOccurrences}
                          max={maximumOccurrences}
                        />
                      </Form.Item>
                    );
                  }

                  return null;
                }}
              </Form.Item>
            </>
          );
        }}
      </Form.Item>
    </>
  );
};

/** Custom day-of-week toggle button group (multi-select) */
const DayOfWeekToggle = ({
  value = [],
  onChange,
  requiredDay,
}: {
  value?: string[];
  onChange?: (value: string[]) => void;
  requiredDay?: string;
}) => {
  const toggle = (day: string) => {
    if (day === requiredDay) return;
    const next = value.includes(day)
      ? value.filter((d) => d !== day)
      : [...value, day];
    onChange?.(next);
  };

  return (
    <Flex gap={10} wrap="wrap" style={{ width: "100%" }}>
      {DAY_OPTIONS.map((opt) => {
        const isRequired = opt.value === requiredDay;
        const isSelected = isRequired || value.includes(opt.value);
        const button = (
          <Button
            key={opt.value}
            type={isSelected ? "primary" : "default"}
            size="small"
            onClick={() => toggle(opt.value)}
            style={{
              minWidth: 55,
              height: 40,
              fontSize: textPx(12),
              cursor: isRequired ? "not-allowed" : undefined,
            }}
          >
            {translate(`label.day_short.${opt.value.toLowerCase()}`)}
          </Button>
        );

        if (isRequired) {
          return (
            <Tooltip
              key={opt.value}
              title={translate("schedule.recurrence.required_day")}
            >
              {button}
            </Tooltip>
          );
        }
        return button;
      })}
    </Flex>
  );
};
